import React from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import styles from './FinalResultModal.module.css';

const FinalResultModal = ({ event, onClose }) => {
  if (!event?.liveSession?.scorecard) {
    return null;
  }

  const { liveSession, teams, finalResults } = event;
  const { scorecard } = liveSession;
  const { score, statistics, events } = scorecard;

  // Determine winner with null safety
  const getWinnerText = () => {
    const homeName = teams?.home?.name || 'Home Team';
    const awayName = teams?.away?.name || 'Away Team';
    
    if (!finalResults?.winner) {
      if (score.home === score.away) {
        return '🤝 MATCH ENDED IN A TIE 🤝';
      }
      return score.home > score.away 
        ? `🏆 ${homeName.toUpperCase()} WIN! 🏆`
        : `🏆 ${awayName.toUpperCase()} WIN! 🏆`;
    }

    if (finalResults.winner === 'tie') {
      return '🤝 MATCH ENDED IN A TIE 🤝';
    }

    const winnerName = finalResults.winner === 'home' 
      ? homeName
      : awayName;

    if (finalResults.decidedInOvertime) {
      return `🏆 ${winnerName.toUpperCase()} WIN IN EXTRA TIME! 🏆`;
    }

    if (finalResults.winningMethod === 'penalties') {
      return `🏆 ${winnerName.toUpperCase()} WIN ON PENALTIES! 🏆`;
    }

    return `🏆 ${winnerName.toUpperCase()} WIN! 🏆`;
  };

  // Calculate total duration
  const getTotalDuration = () => {
    if (!score.periods || score.periods.length === 0) return '0';
    
    let totalMinutes = 0;
    score.periods.forEach(period => {
      if (period.finishedAt && period.startedAt) {
        const duration = new Date(period.finishedAt) - new Date(period.startedAt);
        totalMinutes += Math.floor(duration / 60000);
      }
    });
    
    return totalMinutes || '90';
  };

  // Get top scorer
  const getTopScorer = () => {
    if (!events || events.length === 0) return null;
    
    const goalEvents = events.filter(e => e.type === 'goal');
    if (goalEvents.length === 0) return null;

    // Count goals per player
    const scorers = {};
    goalEvents.forEach(event => {
      const match = event.description.match(/Player #(\d+)/);
      if (match) {
        const player = `#${match[1]}`;
        scorers[player] = (scorers[player] || 0) + 1;
      }
    });

    // Find top scorer
    let topScorer = null;
    let maxGoals = 0;
    Object.entries(scorers).forEach(([player, goals]) => {
      if (goals > maxGoals) {
        maxGoals = goals;
        topScorer = player;
      }
    });

    return topScorer ? `${topScorer} (${maxGoals} goal${maxGoals > 1 ? 's' : ''})` : null;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const homeStats = statistics?.home || {};
  const awayStats = statistics?.away || {};
  const totalYellowCards = (homeStats.yellowCards || 0) + (awayStats.yellowCards || 0);
  const totalRedCards = (homeStats.redCards || 0) + (awayStats.redCards || 0);

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Compact Header */}
        <div className={styles.header}>
          <span className={styles.title}>
            {event.title} • {formatDateTime(liveSession.finishedAt || new Date())}
          </span>
          <button onClick={onClose} className={styles.closeButton}>
            <FaTimes />
          </button>
        </div>

        {/* Winner Announcement + Score */}
        <div className={styles.winnerSection}>
          <span className={styles.winnerText}>{getWinnerText()}</span>
          <span className={styles.finalScore}>
            {teams?.home?.name || 'Home Team'} {score.home} : {score.away} {teams?.away?.name || 'Away Team'}
          </span>
        </div>

        {/* Compact 3-Column Grid */}
        <div className={styles.infoGrid}>
          {/* Column 1: Periods */}
          <div className={styles.column}>
            <div className={styles.columnHeader}>PERIODS</div>
            {score.periods && score.periods.map((period, index) => (
              <div key={index} className={styles.row}>
                <span>{period.name}</span>
                <span>{period.homeScore}-{period.awayScore}</span>
              </div>
            ))}
            <div className={styles.row}>
              <strong>Total</strong>
              <strong>{score.home}-{score.away}</strong>
            </div>
            {finalResults?.margin && (
              <div className={styles.rowSubtle}>
                Margin: +{Math.abs(finalResults.margin)}
              </div>
            )}
          </div>

          {/* Column 2: Statistics - Dynamic based on template */}
          <div className={styles.column}>
            <div className={styles.columnHeader}>STATISTICS</div>
            {Object.keys(homeStats).length > 0 ? (
              <>
                {/* Show top 5 most important stats */}
                {homeStats.possession !== undefined && (
                  <div className={styles.row}>
                    <span>Possess</span>
                    <span>{homeStats.possession}% | {awayStats.possession}%</span>
                  </div>
                )}
                {homeStats.shots !== undefined && (
                  <div className={styles.row}>
                    <span>Shots</span>
                    <span>{homeStats.shots} | {awayStats.shots}</span>
                  </div>
                )}
                {homeStats.shotsOnTarget !== undefined && (
                  <div className={styles.row}>
                    <span>On Target</span>
                    <span>{homeStats.shotsOnTarget} | {awayStats.shotsOnTarget}</span>
                  </div>
                )}
                {homeStats.corners !== undefined && (
                  <div className={styles.row}>
                    <span>Corners</span>
                    <span>{homeStats.corners} | {awayStats.corners}</span>
                  </div>
                )}
                {homeStats.rebounds !== undefined && (
                  <div className={styles.row}>
                    <span>Rebounds</span>
                    <span>{homeStats.rebounds} | {awayStats.rebounds}</span>
                  </div>
                )}
                {homeStats.assists !== undefined && (
                  <div className={styles.row}>
                    <span>Assists</span>
                    <span>{homeStats.assists} | {awayStats.assists}</span>
                  </div>
                )}
                {homeStats.turnovers !== undefined && (
                  <div className={styles.row}>
                    <span>Turnovers</span>
                    <span>{homeStats.turnovers} | {awayStats.turnovers}</span>
                  </div>
                )}
                {homeStats.powerPlays !== undefined && (
                  <div className={styles.row}>
                    <span>Power Plays</span>
                    <span>{homeStats.powerPlays} | {awayStats.powerPlays}</span>
                  </div>
                )}
                {homeStats.penaltyMinutes !== undefined && (
                  <div className={styles.row}>
                    <span>Penalties</span>
                    <span>{homeStats.penaltyMinutes}m | {awayStats.penaltyMinutes}m</span>
                  </div>
                )}
                {homeStats.hits !== undefined && (
                  <div className={styles.row}>
                    <span>Hits</span>
                    <span>{homeStats.hits} | {awayStats.hits}</span>
                  </div>
                )}
                {/* Tennis Stats */}
                {homeStats.aces !== undefined && (
                  <div className={styles.row}>
                    <span>Aces</span>
                    <span>{homeStats.aces} | {awayStats.aces}</span>
                  </div>
                )}
                {homeStats.doubleFaults !== undefined && (
                  <div className={styles.row}>
                    <span>Double Faults</span>
                    <span>{homeStats.doubleFaults} | {awayStats.doubleFaults}</span>
                  </div>
                )}
                {homeStats.firstServePercentage !== undefined && (
                  <div className={styles.row}>
                    <span>1st Serve %</span>
                    <span>{homeStats.firstServePercentage}% | {awayStats.firstServePercentage}%</span>
                  </div>
                )}
                {homeStats.breakPoints !== undefined && (
                  <div className={styles.row}>
                    <span>Break Points</span>
                    <span>{homeStats.breakPoints} | {awayStats.breakPoints}</span>
                  </div>
                )}
                {homeStats.winners !== undefined && (
                  <div className={styles.row}>
                    <span>Winners</span>
                    <span>{homeStats.winners} | {awayStats.winners}</span>
                  </div>
                )}
                {homeStats.unforcedErrors !== undefined && (
                  <div className={styles.row}>
                    <span>Unforced Errors</span>
                    <span>{homeStats.unforcedErrors} | {awayStats.unforcedErrors}</span>
                  </div>
                )}
                {/* American Football Stats */}
                {homeStats.touchdowns !== undefined && (
                  <div className={styles.row}>
                    <span>Touchdowns</span>
                    <span>{homeStats.touchdowns} | {awayStats.touchdowns}</span>
                  </div>
                )}
                {homeStats.fieldGoals !== undefined && (
                  <div className={styles.row}>
                    <span>Field Goals</span>
                    <span>{homeStats.fieldGoals} | {awayStats.fieldGoals}</span>
                  </div>
                )}
                {homeStats.totalYards !== undefined && (
                  <div className={styles.row}>
                    <span>Total Yards</span>
                    <span>{homeStats.totalYards} | {awayStats.totalYards}</span>
                  </div>
                )}
                {homeStats.passingYards !== undefined && (
                  <div className={styles.row}>
                    <span>Pass Yards</span>
                    <span>{homeStats.passingYards} | {awayStats.passingYards}</span>
                  </div>
                )}
                {homeStats.rushingYards !== undefined && (
                  <div className={styles.row}>
                    <span>Rush Yards</span>
                    <span>{homeStats.rushingYards} | {awayStats.rushingYards}</span>
                  </div>
                )}
                {homeStats.firstDowns !== undefined && (
                  <div className={styles.row}>
                    <span>1st Downs</span>
                    <span>{homeStats.firstDowns} | {awayStats.firstDowns}</span>
                  </div>
                )}
                {homeStats.sacks !== undefined && (
                  <div className={styles.row}>
                    <span>Sacks</span>
                    <span>{homeStats.sacks} | {awayStats.sacks}</span>
                  </div>
                )}
                {homeStats.interceptions !== undefined && (
                  <div className={styles.row}>
                    <span>Interceptions</span>
                    <span>{homeStats.interceptions} | {awayStats.interceptions}</span>
                  </div>
                )}
                {homeStats.fumbles !== undefined && (
                  <div className={styles.row}>
                    <span>Fumbles</span>
                    <span>{homeStats.fumbles} | {awayStats.fumbles}</span>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.rowSubtle}>No statistics recorded</div>
            )}
          </div>

          {/* Column 3: Summary */}
          <div className={styles.column}>
            <div className={styles.columnHeader}>SUMMARY</div>
            <div className={styles.row}>
              <span>⏱️ Duration</span>
              <span>{getTotalDuration()} min</span>
            </div>
            <div className={styles.row}>
              <span>⚽ Goals</span>
              <span>{score.home}-{score.away}</span>
            </div>
            {(totalYellowCards > 0 || totalRedCards > 0) && (
              <div className={styles.row}>
                <span>🟨 Cards</span>
                <span>{homeStats.yellowCards || 0}-{awayStats.yellowCards || 0}</span>
              </div>
            )}
            {getTopScorer() && (
              <div className={styles.row}>
                <span>🌟 Top Scorer</span>
                <span>{getTopScorer()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Events Timeline */}
        {events && events.length > 0 && (
          <div className={styles.eventsSection}>
            <div className={styles.eventsHeader}>EVENTS</div>
            <div className={styles.eventsList}>
              {[...events].reverse().slice(0, 8).map((evt, index) => {
                // Multi-sport event icons
                const eventType = {
                  // Soccer
                  'goal': '⚽',
                  'yellowCard': '🟨',
                  'redCard': '🟥',
                  'substitution': '🔄',
                  'corner': '🚩',
                  'penalty': '⚽',
                  'freeKick': '⚽',
                  
                  // Basketball
                  'basket': '🏀',
                  'threePointer': '🏀',
                  'freeThrow': '🏀',
                  'rebound': '🏀',
                  'assist': '🤝',
                  'steal': '🏃',
                  'block': '🛡️',
                  'foul': '🟨',
                  'technicalFoul': '🟥',
                  'timeout': '⏱️',
                  
                  // Hockey
                  'puck': '🏒',
                  'powerPlay': '⚡',
                  'penaltyShot': '🏒',
                  'hockeyHit': '💥',
                  'save': '🥅',
                  
                  // Baseball
                  'homeRun': '⚾',
                  'baseballHit': '⚾',
                  'strikeout': '❌',
                  'walk': '🚶',
                  'run': '🏃',
                  
                  // Tennis
                  'ace': '🎾',
                  'doubleFault': '❌',
                  'winner': '🎾',
                  'breakPoint': '🎾',
                  'gameWon': '✓',
                  'setWon': '✅',
                  
                  // American Football
                  'touchdown': '🏈',
                  'fieldGoal': '🏈',
                  'extraPoint': '🏈',
                  'safety': '🏈',
                  'interception': '🏈',
                  'fumble': '🏈',
                  'sack': '💥',
                  'footballPenalty': '🚩',
                  
                  // Generic
                  'score': '🎯',
                  'point': '📍',
                }[evt.type] || '📝';

                const teamName = evt.team === 'home' 
                  ? (teams?.home?.name || 'Home Team')
                  : (teams?.away?.name || 'Away Team');

                return (
                  <div key={index} className={styles.eventItem}>
                    <span className={styles.eventMinute}>{evt.minute}'</span>
                    <span className={styles.eventIcon}>{eventType}</span>
                    <span className={styles.eventDescription}>
                      {teamName} - {evt.description}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default FinalResultModal;

